// Resolving a named cloud preset off the config alone.
//
// The live page resolves a preset while reading its report param, against the page shell's own
// descriptor. The headless export route has neither: it is handed a config bag with a preset token
// in it and has to reproduce the same look from the ported tables.
//
// What a preset owns, it owns whole. A stored cloud is either "this preset" or "these fields" --
// changing a preset-owned field by hand drops the cloud to the custom preset -- so a token next to
// an explicit override is not a state the page can produce, and the token wins. The fields a preset
// does not own (the mask, the black list, the active word, the shuffle index, the word filters)
// pass through untouched.

// The report's colour themes, which a preset can name by number instead of listing colours — the
// same table the colour-options panel offers the user.
import { COLOR_THEMES } from '@aytm/app/stats/lib/render/colors'
import {
  CUSTOM_PRESET_ID,
  PRESET_OWNED_FIELDS
} from '@aytm/app/stats/page/question-views/components/word-cloud/constants/presetFields'
import {
  getPresetById,
  PRESET_GRADIENTS
} from '@aytm/app/stats/page/question-views/components/word-cloud/constants/presets'
import { resolveMaskToken } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/masks'

// The corpus sizes the font floor is interpolated between, and the floor a small corpus starts from.
const CORPUS_DOMAIN = [10, 150]
export const RANGE_START_TO = 35

/**
 * The floor of the font range follows the corpus: a small corpus draws its words large, and a large
 * one is allowed down to the preset's own floor. Shared with the report-param grammar so both
 * resolve a preset to the same font sizes.
 *
 * @param {number} corpusSize - how many words the cloud has to place
 * @param {number} from - the floor a small corpus starts from
 * @param {number} to - the preset's own floor
 * @returns {number}
 */
export const scaleRangeStart = (corpusSize, from, to) => {
  const [low, high] = CORPUS_DOMAIN
  const value = Math.round(from + (to - from) * ((corpusSize - low) / (high - low)))

  if (value < to) return to
  if (value > from) return from

  return value
}

// A preset names its palette either outright or by number: a gradient by its stops, anything else by
// the report's colour theme.
const presetColors = preset => {
  const { colorsIndex, colorStyle } = preset

  if (Array.isArray(colorsIndex)) return [...colorsIndex]
  if ('gradient' === colorStyle) return [...(PRESET_GRADIENTS[colorsIndex - 1]?.[0] || [])]

  return (COLOR_THEMES[colorsIndex - 1] || []).slice(0, 7)
}

const withoutPresetFields = config => {
  const rest = {}

  for (const key of Object.keys(config)) {
    if (PRESET_OWNED_FIELDS.includes(key)) continue
    rest[key] = config[key]
  }

  return rest
}

/**
 * Apply the preset a config points at, if it points at one.
 *
 * @param {Object} [wcConfig] - the config bag as handed in, preset token and all
 * @param {number} [wordsCount] - the corpus size, which caps the word count and scales the font floor
 * @returns {Object} a new config: the preset's look over the fields the preset does not own
 */
export const applyPreset = (wcConfig = {}, wordsCount = 0) => {
  const config = { ...wcConfig }

  if (undefined !== config.mask) config.mask = resolveMaskToken(config.mask)

  const id = config.currentPresetId
  if (null == id || CUSTOM_PRESET_ID === id) return config

  const preset = getPresetById(id)

  // A token the ported table does not offer: the cloud keeps the id it was told to and a cap left at
  // zero opens up to the whole corpus, as the page does with a preset it no longer has.
  if (!preset) {
    if (0 === config.maxWords) config.maxWords = wordsCount

    return config
  }

  return {
    ...withoutPresetFields(config),
    currentPresetId: id,
    scaleFunction: preset.scaleFunction,
    fontFamily: preset.fontFamily,
    angles: preset.angles.map(Number),
    colors: presetColors(preset),
    background: preset.background,
    colorStyle: preset.colorStyle,
    rangeStart: scaleRangeStart(wordsCount, RANGE_START_TO, preset.rangeStart),
    rangeEnd: preset.rangeEnd,
    // A corpus the caller could not count yet leaves the preset's own cap standing — capping to
    // zero would empty the cloud instead of limiting it.
    maxWords: 0 < wordsCount ? Math.min(preset.maxWords, wordsCount) : preset.maxWords,
    orientation: preset.orientation
  }
}

export default applyPreset
